import React from 'react'
import { Box, CircularProgress, Collapse, Switch, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { SectionHeading, SectionSubHeading, PairingButton } from 'src/Libs/flow/components'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { formatSki } from '../../utils/format-ski'
import { useSmartMeterGateway } from '../hooks/use-smart-meter-getaway'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { SettingsBox } from 'src/Elements/Data'
import { useStyles } from 'src/Components/toggle/Toggle'
import { EEBusMiddlewareDto } from '../../charging-stations'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

export const SmartMeterGateway: React.FC = () => {
  const { t } = useCustomTranslation()
  const { control } = useInstallationForm()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const installationId = useWatch({ control, name: 'id' })
  const [emcDeviceId, emcIsPaired] = useWatch({
    control,
    name: ['emcDevice.deviceId', 'emcDevice.isPaired'],
  })

  const { data, isActive, isLoading, isSmartMeterLoading, isEmpty, isError, isPairingDisabled, toggleIsActive, handlePairingClick } =
    useSmartMeterGateway({
      installationId: installationId ?? -1,
      emcDeviceId,
      emcIsPaired,
    })
  const { classes } = useStyles({ disabled: isLoading || !canEdit })

  if (installationId === null || !emcDeviceId || !emcDeviceId || isSmartMeterLoading || isError || data === undefined) return null

  return (
    <section>
      <SectionHeading title={t('installation.smartMeterGateway.smart-meter-gateway')} />

      <section>
        <SettingsBox
          title={t('installation.smartMeterGateway.smart-meter-gateway-active-info')}
          suffix={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isLoading && <CircularProgress size={26} />}
              <Switch
                className={classes.switchStyle}
                checked={!!data?.smartMeterGateway?.active}
                onChange={toggleIsActive}
                disabled={isLoading || !canEdit}
              />
            </Box>
          }
        />
      </section>

      <Collapse in={isActive} mountOnEnter={true} unmountOnExit={true}>
        {!!data?.eebusController && (
          <Box component={'section'} pt={3}>
            <SmartMeterGatewayEebusController eebus={data.eebusController} />
          </Box>
        )}

        <Box component={'section'} pt={3}>
          <SectionSubHeading title={t('installation.smartMeterGateway.smart-meter-gateway-information')} />

          <Box sx={{ mb: 2, borderBottom: theme => `1px solid ${theme.palette.divider}` }} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <SettingsBox
              title={t('productEditor.pairingEditor.pairingID.smgw')}
              suffix={
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <TextField
                    label={t('productEditor.pairingEditor.pairingID.smgw')}
                    value={formatSki(data?.smartMeterGateway.ski)}
                    disabled
                    clearable={false}
                    useDebounceDelay={false}
                    fullWidth
                  />
                </Box>
              }
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PairingButton
                size="large"
                sx={{ height: '100%', width: { xs: '100%', md: 'auto' } }}
                icon={isEmpty ? 'link' : 'transfer'}
                onClick={handlePairingClick}
                disabled={isPairingDisabled}
              >
                {isEmpty ? t('common.edit') : t('common.replace')}
              </PairingButton>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </section>
  )
}

const SmartMeterGatewayEebusController: React.FC<{ eebus: EEBusMiddlewareDto }> = ({ eebus }) => {
  const { t } = useCustomTranslation()

  return (
    <>
      <SectionSubHeading title={t('installation.configOverview.eebus-controller-information')} />
      <Box sx={{ mb: 2, borderBottom: theme => `1px solid ${theme.palette.divider}` }} />
      <SettingsBox title={t('common.name')} suffix={<Typography>{eebus.deviceName}</Typography>} />
      <SettingsBox title={t('installation.smartMeterGateway.ship-id')} suffix={<Typography>{eebus.shipId}</Typography>} />
      <SettingsBox title={t('installation.smartMeterGateway.ski')} suffix={<Typography>{formatSki(eebus.ski)}</Typography>} />
    </>
  )
}
